import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Radio } from "lucide-react";
import { useCountry } from "@/lib/CountryContext";
import { fmtPrice } from "@/lib/utils";
import { logger } from "@/lib/logger";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const MIN_INTERVAL_MS = 30_000; // throttle: max 1 live toast every 30 s

/**
 * Server-Sent Events listener for newly-created annonces.
 *
 * Opens a persistent EventSource to `/api/stream/annonces?country=XX`. Each
 * `annonce.created` event triggers a sonner toast with a "Voir" action.
 * Auto-reconnects via the browser's built-in EventSource retry behaviour.
 */
export default function useLiveAnnonceStream() {
  const navigate = useNavigate();
  const { code, country } = useCountry();

  useEffect(() => {
    if (!API_BASE) return;
    const url = `${API_BASE}/api/stream/annonces?country=${encodeURIComponent(code)}`;
    let lastShownAt = 0;
    let es;
    try {
      es = new EventSource(url, { withCredentials: true });
    } catch (e) {
      logger.warn("SSE EventSource init failed", e);
      return;
    }
    const handler = (ev) => {
      try {
        const ann = JSON.parse(ev.data);
        // Server-side already filters by country, but double-check defensively.
        if (ann.country && ann.country !== code) return;
        const now = Date.now();
        if (now - lastShownAt < MIN_INTERVAL_MS) return;
        lastShownAt = now;
        toast(
          `LIVE ${country.flag} ${ann.city} — ${ann.category || "Annonce"}`,
          {
            description: `${ann.title} · ${fmtPrice(ann.price, ann.price_unit)}`,
            icon: <Radio size={18} className="text-emerald-600" />,
            duration: 6000,
            action: {
              label: "Voir",
              onClick: () => navigate(`/annonces/${ann.id}`),
            },
            className: `live-annonce-toast-${code}`,
          }
        );
      } catch (e) {
        logger.warn("SSE message parse failed", e);
      }
    };
    es.addEventListener("annonce.created", handler);
    es.onerror = () => {
      // Browser auto-reconnects. Log once for debugging.
      logger.warn("SSE connection error — will auto-reconnect");
    };
    return () => {
      es.removeEventListener("annonce.created", handler);
      es.close();
    };
  }, [code, country.flag, country.name, navigate]);
}
